<script module lang="ts">
  import type { LucideIcon } from '@lucide/svelte';
  import { type AllowedIcon, type IconProps as Props } from './icons.types';
</script>

<script lang="ts">
  import * as Icons from '@lucide/svelte';

  const { icon, size = 0.8, alarm, count, children }: Props = $props();

  const IconsMapper = {
    save: Icons.Save,
    cancel: Icons.X,
    settings: Icons.Settings,
  } satisfies Record<AllowedIcon, LucideIcon>;

  const IconComp = $derived(typeof icon === 'string' ? IconsMapper[icon] : icon);
  const IconSize = $derived(Math.min(Math.max(0.1, size), 5));
  const IconLabel = $derived(children);
  const HasLabel = $derived(!!IconLabel);

  // - the number is capped, so the badge keeps its size
  const MaxCount = 99;
  const CountLabel = $derived(count !== undefined && count > 0 ? (count > MaxCount ? `${String(MaxCount)}+` : String(Math.round(count))) : null);
</script>

<span class="lyIconWrapper {HasLabel && 'lyIconWithLabel'}" style:--style-size={IconSize}>
  <span class="lyIconBadges">
    <IconComp class="lyIconsInlay" />
    {#if alarm}
      <span class="lyIconAlarm" aria-hidden="true"></span>
    {/if}
    {#if CountLabel !== null}
      <span class="lyIconCount" aria-hidden="true">{CountLabel}</span>
    {/if}
  </span>
  {#if IconLabel}
    <span class="lyIconLabel">{@render IconLabel()}</span>
  {/if}
</span>

<style lang="scss">
  .lyIconWrapper {
    // - centred on the text instead of sitting on its baseline (an inline svg keeps room for descenders below)
    display: inline-flex;
    vertical-align: middle;

    :global(.lyIconsInlay) {
      display: block;
      width: calc(var(--style-size, 1) * 1em);
      height: auto;
      margin: 0;
      padding: 0;
    }

    .lyIconBadges {
      position: relative;
      display: inline-flex;
    }

    .lyIconAlarm {
      position: absolute;
      top: 0;
      right: 0;
      width: 0.45em;
      height: 0.45em;
      border-radius: 50%;
      background-color: var(--color-warning-secondary);
      box-shadow: 0 0 0 1px var(--layout-background);
      transform: translate(25%, -25%);
    }

    .lyIconCount {
      position: absolute;
      right: 0;
      bottom: 0;
      min-width: 1.4em;
      padding: 0.1em 0.3em;
      box-sizing: border-box;
      border-radius: 0.7em;
      background-color: var(--color-notice-secondary);
      color: #fff;
      font-size: 0.5em;
      line-height: 1.2;
      text-align: center;
      transform: translate(35%, 35%);
    }

    &.lyIconWithLabel {
      display: inline-flex;
      align-items: center;
      gap: 0.25em;

      :global(.lyIconsInlay) {
        flex-shrink: 0;
      }
    }
  }
</style>
